import { useState } from "react";
import { useTicket } from "@/context/TicketContext";
import { Comment, CommentAction, TicketAction, useUpdateTicketCommentMutation } from "@/services/LiveQueueService";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { Textarea } from "@/components/ui/TextArea";
import { Send } from "lucide-react";
import {Button} from "@/components/ui/Button";
interface CommentsSectionProps {
  ticketId: string;
  refetchComments?: () => void;
  ticketData?: Comment[];
}

const CommentsSection: React.FC<CommentsSectionProps> = ({ ticketId ,refetchComments,ticketData}) => {
  const [newComment, setNewComment] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [updateTicket] = useUpdateTicketCommentMutation();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);

    const {
      comments,
      addComment: addCommentContext,
      updateComment: updateCommentContext,
      deleteComment: deleteCommentContext,
      fetchComments,
      currentTicket,
    } = useTicket();

  const activeTicketId=currentTicket?.id; 


  


  const addComment = async () => {
    if (!activeTicketId) {
      setError("No ticket selected.");
      return;
    }

    if (!newComment.trim()) return;

    if (newComment.length > 350) {
      setError("Comment cannot exceed 350 characters.");
      return;
    }

    try {
      setIsAdding(true);
      setError(null);
      await addCommentContext(Number(activeTicketId), newComment);
      setNewComment("");
      refetchComments && refetchComments();
    } catch (error) {
      setError("Failed to add comment. Please try again.");
    } finally {
      setIsAdding(false);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const comment = e.target.value;
    if (comment.length > 350) {
      setError("Comment cannot exceed 350 characters.");
    } else {
      setError("");
    }
    setNewComment(comment);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Textarea
          value={newComment}
          // onChange={(e) => setNewComment(e.target.value)}
          onChange={handleCommentChange}
          className="flex-1 p-2 border rounded h-20"
          placeholder="Add a comment..."
        />
        <button
          onClick={addComment}
          disabled={isAdding}
          aria-label="Add comment"
          className="flex items-center gap-2 text-[#C1554E] text-sm font-medium"
        >
          {isAdding ? (
            <i className="fa fa-spinner animate-spin"></i>
          ) : (
            
              <Send className="w-5 h-5" />
          )}
        </button>
      </div>

      {error && <p className="text-red-500">{error}</p>}

      {/* {currentTicket?.comments
        ?.filter((comment) => comment.created_at) // Remove comments with missing created_at
        .sort((a, b) => b.id - a.id)
        .map((comment: Comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            onUpdate={updateCommentContext}
            onDelete={deleteCommentContext}
            ticketId={activeTicketId as unknown as number}
            realTicketId={ticketId}
            refetchComments={refetchComments} // Pass the refetchComments function to CommentItem
          />
        ))} */}
        
      {(currentTicket?.comments)
        ?.filter((comment) => comment.created_at)
        .sort((a, b) => b.id - a.id)
        .map((comment: Comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            onUpdate={updateCommentContext}
            onDelete={deleteCommentContext}
            ticketId={activeTicketId as unknown as number}
            realTicketId={ticketId}
            refetchComments={refetchComments}
          />
        ))}
      {/* {ticketData
        ?.filter((comment) => comment.created_at) // Remove comments with missing created_at
        .sort((a, b) => b.id - a.id)
        .map((comment: Comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            onUpdate={updateCommentContext}
            onDelete={deleteCommentContext}
            ticketId={activeTicketId as unknown as number}
            realTicketId={ticketId}
            refetchComments={refetchComments} // Pass the refetchComments function to CommentItem
          />
        ))} */}
    </div>
  );
};

interface CommentItemProps {
comment: Comment;
ticketId:number;
realTicketId?: string;
onUpdate: (ticketId: number, commentId: number, content: string) => void;
onDelete: (ticketId: number, commentId: number) => void;
refetchComments?: () => void;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onUpdate,
  onDelete,
  ticketId,
  refetchComments,
  realTicketId
}) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedContent, setEditedContent] = useState<string>(comment.comment);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [updateTicket] = useUpdateTicketCommentMutation();

  const { currentTicket } = useTicket()

  const handleUpdate = async () => {
    if (editedContent.trim().length === 0) {
      setError("Comment cannot be empty.");
      return;
    }
    if (editedContent.length > 350) {
      setError("Comment cannot exceed 350 characters.");
      return;
    }
    try {
      setIsUpdating(true);
      // await onUpdate(Number(currentTicket?.id), comment.id, editedContent);
      await onUpdate(ticketId, comment.id, editedContent);
      refetchComments && refetchComments(); // Call the refetchComments function after updating
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating comment:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      // await onDelete(Number(currentTicket?.id), comment.id);
      await onDelete(ticketId, comment.id);

      
      refetchComments && refetchComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setIsDeleting(false);
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditedContent(comment.comment);
    setError("");
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const comment = e.target.value;
    if (comment.length > 350) {
      setError("Comment cannot exceed 350 characters.");
    } else {
      setError("");
    }
    setEditedContent(comment);
  };
  return (
    <div className="p-2 border rounded">
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            value={editedContent}
            // onChange={(e) => setEditedContent(e.target.value)}
            onChange={handleCommentChange}
            className="w-full p-2 border rounded"
            rows={3}
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex gap-2">
            <Button
              variant={"outline"}
              onClick={handleUpdate}
              disabled={isUpdating}
              className="px-4 py-2 text-sm w-fit  rounded"
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
            <Button
              variant={"outline"}
              onClick={handleCancel}
              className="px-4 py-2 text-sm  rounded w-fit"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className=" flex flex-row justify-between items-center">
          <div className="flex items-center gap-2 max-w-[85%]">
            {/* Profile Icon */}
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white">
              <span className="text-sm w-fit">
                {comment.staff_username.substring(0, 3)}
              </span>{" "}
            </div>
            {/* <div> 
              <p className="text-xs text-gray-500">Created by</p>
              <p className="text-sm font-semibold">{comment.created_by}</p>
            </div> */}
            {/* <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild className="text-sm">
                  <p>
                    {comment.comment.length > 20
                      ? `${comment.comment.substring(0, 20)}...`
                      : comment.comment}
                  </p>
                </TooltipTrigger>
                <TooltipContent className="h-fit max-w-64 ">
                  <span className="break-all text-sm">{comment.comment}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider> */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* <p className="text-sm max-w-[200px] truncate overflow-hidden"> */}
                  <p className="text-sm max-w-full line-clamp-3 whitespace-pre-line break-all">
                    {comment.comment}
                  </p>
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px] whitespace-normal">
                  <p className="break-all text-sm whitespace-pre-wrap overflow-wrap-anywhere">
                    {comment.comment}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {comment.created_by === userInfo?.id && (
            <div className="flex gap-2 items-center justify-center">
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm text-gray-500"
              >
                <i className="fa-solid fa-pencil pr-2"></i>
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="text-sm text-red-500"
              >
                {isDeleting ? (
                  <i className="fa-solid fa-spinner  animate-spin pr-2"></i>
                ) : (
                  <i className="fa-solid fa-trash pr-2"></i>
                )}
              </button>
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end  w-full flex-col items-end">
        <p className="text-xs text-gray-500">{comment.staff_username}</p>
        <p className="text-xs text-gray-500">
          {(() => {
            const date = new Date(comment.created_at + "Z");
            const formattedDate = date.toLocaleDateString("en-PK", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }).replace(/\//g, "-");
            const formattedTime = date.toLocaleTimeString("en-PK", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
            return `${formattedDate} ${formattedTime}`;
          })()}
        </p>
      </div>
    </div>
  );
};

export default CommentsSection;